import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CustomSnackbar from "../SnackBar";
import useSnackbar from "../SnackbarHook";

import * as Yup from "yup";
import { Formik, FormikHelpers } from "formik";
import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import { spacing } from "@mui/system";
import {
  Edit,
  Delete,
  Add,
  AttachFile,
  Close,
  Download,
  Visibility,
  Search,
} from "@mui/icons-material";

import {
  fetchNotices,
  createNotice,
  updateNotice,
  deleteNotice,
  sendNotice,
  Notice,
  NoticeAttachment,
  openNoticeForm,
  setNotice,
  SendNoticeResponse,
} from "../../../redux/slices/notice";
import {
  fetchNoticeDeliveries,
  patchDelivery,
  clearDeliveries,
  NoticeDelivery,
  NoticeDeliveryStatus,
} from "../../../redux/slices/noticeDelivery";
import { uploadFile } from "../../../redux/slices/file";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import useAuth from "../../../hooks/useAuth";
import { usePermission } from "../../../hooks/usePermission";
import { format } from "date-fns";
import { baseUrl } from "../../../config";
import PDFViewerModal from "../../PDFViewer";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Button = styled(MuiButton)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

// Notices are always sent to these roles; there is no per-notice target
// group selection.
const DEFAULT_TARGET_ROLES = ["ADMIN", "MEMBER", "ACCOUNTANT", "CONTRACTOR"];

const PREVIEWABLE_MIMETYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
]);

function isPreviewable(att: {
  mimetype?: string;
  originalname: string;
}): boolean {
  if (att.mimetype) return PREVIEWABLE_MIMETYPES.has(att.mimetype);
  const ext = att.originalname.split(".").pop()?.toLowerCase();
  return ["pdf", "jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(
    ext ?? ""
  );
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required("Otsikko on pakollinen"),
  content: Yup.string().required("Sisältö on pakollinen"),
});

interface NoticeFormValues {
  title: string;
  content: string;
  targetRoles: string[];
  submit?: string;
}

function getDeliveryStatusInfo(status: NoticeDeliveryStatus | undefined): {
  label: string;
  color: ChipProps["color"];
  variant?: "filled" | "outlined";
} {
  switch (status) {
    case "queued":
      return { label: "Jonossa", color: "default", variant: "outlined" };
    case "pdf_generating":
      return { label: "Luodaan PDF:ää", color: "info", variant: "outlined" };
    case "ready":
      return { label: "PDF valmis", color: "info", variant: "filled" };
    case "sending_email":
      return { label: "Lähetetään", color: "warning", variant: "filled" };
    case "sent":
      return { label: "Lähetetty", color: "success", variant: "filled" };
    case "failed":
      return { label: "Epäonnistui", color: "error", variant: "filled" };
    default:
      return { label: "Tuntematon", color: "default", variant: "outlined" };
  }
}

// --- NoticeDeliveries inline component ---
function NoticeDeliveriesTable({ noticeId }: { noticeId: number }) {
  const dispatch = useAppDispatch();
  const { deliveries, status } = useAppSelector(
    (state) => state.noticeDelivery
  );

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleDownload = async (filename: string) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Download error:", e);
    }
  };

  useEffect(() => {
    dispatch(fetchNoticeDeliveries(noticeId));
    return () => {
      dispatch(clearDeliveries());
    };
  }, [dispatch, noticeId]);

  if (status === "loading") {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card mb={6}>
      <CardContent pb={1}>
        <Typography variant="h6" gutterBottom>
          Lähetykset
        </Typography>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nimi</TableCell>
              <TableCell>Lähetys Pvm</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deliveries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  Ei lähetyksiä
                </TableCell>
              </TableRow>
            ) : (
              deliveries.map((d) => {
                const { label, color, variant } = getDeliveryStatusInfo(
                  d.status
                );
                return (
                  <TableRow key={d.id} hover>
                    <TableCell>
                      {d.lastName} {d.firstName}
                    </TableCell>
                    <TableCell>
                      {d.sentAt
                        ? format(new Date(d.sentAt), "dd.MM.yyyy HH:mm")
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={label}
                        color={color}
                        variant={variant}
                      />
                      {d.lastError && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ ml: 1 }}
                        >
                          {d.lastError}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {d.file?.filename && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setPreviewUrl(
                                `${baseUrl}/files/preview/${d.file!.filename}`
                              );
                              setPreviewOpen(true);
                            }}
                            title="Esikatsele PDF"
                          >
                            <Search fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(d.file!.filename!)}
                            title="Lataa PDF"
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Paper>
      <PDFViewerModal
        open={previewOpen}
        fileUrl={previewUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
        onClose={() => setPreviewOpen(false)}
      />
    </Card>
  );
}

// --- NoticeForm component ---
function NoticeForm({
  onComplete,
  setOpenSnackbar,
  setSnackbarMessage,
  setSnackbarSeverity,
}: {
  onComplete: (success: boolean) => void;
  setOpenSnackbar: (v: boolean) => void;
  setSnackbarMessage: (v: string) => void;
  setSnackbarSeverity: (v: "success" | "error" | "info" | "warning") => void;
}) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { notice } = useAppSelector((state) => state.notice);
  const associationId = currentUser?.activeAssociation?.id;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const deliveriesSectionRef = useRef<HTMLDivElement>(null);

  const [attachments, setAttachments] = useState<NoticeAttachment[]>(
    notice?.attachments || []
  );
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sentNoticeId, setSentNoticeId] = useState<number | null>(null);
  const esRef = useRef<EventSource | null>(null);
  const finishedIdsRef = useRef<Set<number>>(new Set());
  const expectedCountRef = useRef<number>(0);

  useEffect(() => {
    setAttachments(notice?.attachments || []);
  }, [notice?.id]);

  useEffect(() => {
    return () => {
      if (esRef.current) esRef.current.close();
    };
  }, []);

  const handleAttachmentUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingAttachment(true);
    try {
      const result = await dispatch(
        uploadFile({ file, associationId })
      ).unwrap();
      setAttachments((prev) => [
        ...prev,
        {
          id: result.id,
          originalname: result.originalname,
          filename: result.filename,
        },
      ]);
    } catch {
      setSnackbarMessage("Liitteen lisääminen epäonnistui.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const openNoticeStream = async (
    batchId: string,
    noticeId: number,
    recipients: number
  ) => {
    finishedIdsRef.current = new Set();
    expectedCountRef.current = recipients;

    await dispatch(fetchNoticeDeliveries(noticeId));

    if (esRef.current) esRef.current.close();
    const es = new EventSource(
      `${baseUrl}/notice-deliveries/batch/${batchId}/stream`
    );
    esRef.current = es;

    const maybeFinalize = async () => {
      if (finishedIdsRef.current.size >= expectedCountRef.current) {
        await dispatch(fetchNoticeDeliveries(noticeId));
        es.close();
        if (esRef.current === es) esRef.current = null;
      }
    };

    es.addEventListener("progress", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.deliveryId) return;
      dispatch(
        patchDelivery({
          deliveryId: msg.deliveryId,
          updates: {
            status: msg.status,
            pct: msg.pct,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      // Some backends emit "sent" as a progress update rather than waiting
      // for a "completed" event; treat that as terminal too so the batch
      // still finalizes if the parent job's "completed" event is delayed
      // or dropped.
      if (
        (msg.source === "email" || msg.source === "post") &&
        msg.status === "sent"
      ) {
        if (!finishedIdsRef.current.has(msg.deliveryId)) {
          finishedIdsRef.current.add(msg.deliveryId);
          maybeFinalize();
        }
      }
    });

    es.addEventListener("completed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.deliveryId) return;
      if (msg.source === "file") return;

      dispatch(
        patchDelivery({
          deliveryId: msg.deliveryId,
          updates: {
            status: msg.status ?? "sent",
            sentAt: msg.sentAt ?? new Date().toISOString(),
            pct: 100,
            ...(msg.fileId ? { file: { id: msg.fileId } } : {}),
          },
        })
      );

      if (!finishedIdsRef.current.has(msg.deliveryId)) {
        finishedIdsRef.current.add(msg.deliveryId);
        maybeFinalize();
      }
    });

    es.addEventListener("failed", (e: MessageEvent) => {
      const msg = JSON.parse(e.data);
      if (!msg?.deliveryId) return;
      dispatch(
        patchDelivery({
          deliveryId: msg.deliveryId,
          updates: {
            status: "failed",
            lastError: msg.reason ?? msg.lastError ?? "Tuntematon virhe",
          },
        })
      );
      if (!finishedIdsRef.current.has(msg.deliveryId)) {
        finishedIdsRef.current.add(msg.deliveryId);
        maybeFinalize();
      }
    });

    es.addEventListener("ping", () => {});
    es.addEventListener("error", () => {});
  };

  const handleSubmit = async (
    values: NoticeFormValues,
    {
      resetForm,
      setErrors,
      setStatus,
      setSubmitting,
    }: FormikHelpers<NoticeFormValues>
  ) => {
    try {
      const payload = {
        ...values,
        associationId,
        targetRoles: DEFAULT_TARGET_ROLES,
        attachmentIds: attachments.map((a) => a.id),
      };

      if (notice?.id !== undefined) {
        await dispatch(updateNotice({ id: notice.id, data: payload })).unwrap();
      } else {
        await dispatch(createNotice(payload)).unwrap();
      }

      resetForm();
      setStatus({ sent: true });
      setSubmitting(false);
      dispatch(openNoticeForm(false));
      onComplete(true);
    } catch (error: any) {
      setErrors({
        submit: error?.message || "Virhe tallennettaessa tiedotetta.",
      });
      setStatus({ sent: false });
      setSubmitting(false);
      onComplete(false);
    }
  };

  const handleSaveAndSend = async (
    values: NoticeFormValues,
    { setErrors, setStatus, setSubmitting }: FormikHelpers<NoticeFormValues>
  ) => {
    try {
      const payload = {
        ...values,
        associationId,
        targetRoles: DEFAULT_TARGET_ROLES,
        attachmentIds: attachments.map((a) => a.id),
      };

      let savedNotice: Notice;
      if (notice?.id !== undefined) {
        savedNotice = await dispatch(
          updateNotice({ id: notice.id, data: payload })
        ).unwrap();
      } else {
        savedNotice = await dispatch(createNotice(payload)).unwrap();
      }

      dispatch(setNotice(savedNotice));
      setStatus({ sent: true });
      setSubmitting(false);
      setSendDialogOpen(true);
    } catch (error: any) {
      setErrors({
        submit: error?.message || "Virhe tallennettaessa tiedotetta.",
      });
      setStatus({ sent: false });
      setSubmitting(false);
    }
  };

  const handleConfirmSend = async () => {
    const currentNotice = notice;
    if (!currentNotice?.id || !associationId) return;
    setSending(true);
    try {
      const { batchId, recipients } = await dispatch(
        sendNotice({ associationId, noticeId: currentNotice.id })
      ).unwrap();

      setSendDialogOpen(false);
      setSentNoticeId(currentNotice.id);

      await openNoticeStream(batchId, currentNotice.id, recipients);

      setTimeout(() => {
        deliveriesSectionRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);

      setSnackbarMessage(
        `Tiedotteen lähetys aloitettu (${recipients} vastaanottajaa).`
      );
      setSnackbarSeverity("success");
    } catch (e: any) {
      setSnackbarMessage(e?.message || "Virhe tiedotteen lähetyksessä.");
      setSnackbarSeverity("error");
    } finally {
      setSending(false);
      setOpenSnackbar(true);
    }
  };

  const formInitialValues: NoticeFormValues = {
    title: notice?.title || "",
    content: notice?.content || "",
    targetRoles: notice?.targetRoles || DEFAULT_TARGET_ROLES,
  };

  if (!associationId) {
    return (
      <Card mb={6}>
        <CardContent>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="30vh"
          >
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Formik<NoticeFormValues>
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikHelpers) => {
        const {
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue,
        } = formikHelpers;

        const handleSaveAndSendWrapper = () =>
          handleSaveAndSend(values, formikHelpers);

        return (
          <>
            <Card mb={6}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {notice ? "Muokkaa tiedotetta" : "Luo uusi tiedote"}
                </Typography>

                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        name="title"
                        label="Otsikko"
                        value={values.title}
                        error={Boolean(touched.title && errors.title)}
                        helperText={touched.title && errors.title}
                        fullWidth
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        name="content"
                        label="Sisältö"
                        value={values.content}
                        error={Boolean(touched.content && errors.content)}
                        helperText={touched.content && errors.content}
                        fullWidth
                        multiline
                        rows={8}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        variant="outlined"
                        my={2}
                      />
                    </Grid>
                  </Grid>

                  <Divider my={4} />

                  <Typography variant="subtitle1" gutterBottom>
                    Liitteet
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    {attachments.map((att) => (
                      <Box
                        key={att.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          px: 1.5,
                          py: 0.75,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <AttachFile fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flexGrow: 1 }}>
                          {att.originalname}
                        </Typography>
                        <Tooltip
                          title={
                            isPreviewable(att)
                              ? "Esikatsele liite"
                              : "Esikatselu ei tuettu tälle tiedostotyypille"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              disabled={!isPreviewable(att)}
                              onClick={() => {
                                setPreviewUrl(
                                  `${baseUrl}/files/preview/${att.filename}`
                                );
                                setPreviewOpen(true);
                              }}
                              aria-label="Esikatsele liite"
                            >
                              <Search fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveAttachment(att.id)}
                          aria-label="Poista liite"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleAttachmentUpload}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={
                      uploadingAttachment ? (
                        <CircularProgress size={14} />
                      ) : (
                        <Add />
                      )
                    }
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment}
                  >
                    Lisää liite
                  </Button>

                  <Divider my={4} />

                  {errors.submit && (
                    <Typography color="error" mb={2}>
                      {errors.submit}
                    </Typography>
                  )}

                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                    >
                      Tallenna luonnos
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={isSubmitting || notice?.status === "sent"}
                      onClick={handleSaveAndSendWrapper}
                    >
                      Lähetä tiedote
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => dispatch(openNoticeForm(false))}
                    >
                      {notice?.id ? "Takaisin" : "Peruuta"}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>

            <PDFViewerModal
              open={previewOpen}
              fileUrl={previewUrl ?? ""}
              authToken={localStorage.getItem("accessToken")}
              onClose={() => setPreviewOpen(false)}
            />

            {/* Show deliveries after send, or when viewing an already-sent notice */}
            {(sentNoticeId || notice?.status === "sent") && notice?.id && (
              <div ref={deliveriesSectionRef}>
                <NoticeDeliveriesTable noticeId={sentNoticeId ?? notice.id} />
              </div>
            )}

            {/* Send confirmation dialog */}
            <Dialog
              open={sendDialogOpen}
              onClose={() => !sending && setSendDialogOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Lähetä tiedote</DialogTitle>
              <DialogContent>
                <Typography variant="body1" fontWeight={500} gutterBottom>
                  {values.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tiedote lähetetään sähköpostitse valituille kohderyhmille:{" "}
                  {values.targetRoles.join(", ")}
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                  onClick={() => setSendDialogOpen(false)}
                  disabled={sending}
                  color="inherit"
                >
                  Peruuta
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmSend}
                  disabled={sending}
                  startIcon={
                    sending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {sending ? "Lähetetään..." : "Lähetä"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        );
      }}
    </Formik>
  );
}

// --- NoticeList component ---
function NoticeList({
  rows,
  handleEdit,
  handleDelete,
}: {
  rows: Notice[];
  handleEdit: (notice: Notice | null, isOpen: boolean) => void;
  handleDelete: (notice: Notice) => void;
}) {
  const { currentUser } = useAuth();
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const { hasPermission: canDelete } = usePermission(
    "notice",
    "delete",
    associationId
  );
  const { status } = useAppSelector((state) => state.notice);

  const getStatusChip = (status?: string) => {
    if (status === "sent")
      return <Chip size="small" label="Lähetetty" color="success" />;
    return <Chip size="small" label="Luonnos" variant="outlined" />;
  };

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tiedotteet
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Add />}
          onClick={() => handleEdit(null, true)}
        >
          Lisää tiedote
        </Button>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Aihe</TableCell>
              <TableCell>Kohderyhmä</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Luotu</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Ei tiedotteita
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>
                    {(row.targetRoles || []).map((r) => (
                      <Chip key={r} size="small" label={r} sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                  <TableCell>
                    {row.createdAt
                      ? format(new Date(row.createdAt), "dd.MM.yyyy")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {row.status !== "sent" && (
                      <Edit
                        style={{ cursor: "pointer", marginRight: 8 }}
                        onClick={() => handleEdit(row, true)}
                      />
                    )}
                    {row.status === "sent" && (
                      <Edit
                        style={{ cursor: "pointer", marginRight: 8 }}
                        titleAccess="Näytä lähetykset"
                        onClick={() => handleEdit(row, true)}
                      />
                    )}
                    {canDelete && (
                      <Delete
                        style={{ cursor: "pointer" }}
                        onClick={() => handleDelete(row)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

// --- Notices page ---
function Notices() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const { notices, open } = useAppSelector((state) => state.notice);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleDelete = async (notice: Notice) => {
    const confirmed = window.confirm(
      `Haluatko varmasti poistaa tiedotteen "${notice.title}"?`
    );
    if (confirmed && notice.id) {
      setProgress(100);
      try {
        await dispatch(deleteNotice(notice.id));
        setSnackbarMessage("Tiedote poistettu onnistuneesti!");
        setSnackbarSeverity("success");
      } catch {
        setSnackbarMessage("Virhe poistettaessa tiedotetta.");
        setSnackbarSeverity("error");
      } finally {
        setOpenSnackbar(true);
      }
    }
  };

  const handleEdit = (notice: Notice | null, isOpen: boolean) => {
    dispatch(setNotice(notice));
    dispatch(openNoticeForm(isOpen));
  };

  useEffect(() => {
    if (associationId) {
      dispatch(fetchNotices(associationId));
    }
    return () => {
      dispatch(openNoticeForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <React.Fragment>
      <Helmet title="Tiedotteet" />
      <Typography variant="h3" gutterBottom display="inline">
        Tiedotteet
      </Typography>
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Tiedotteet</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {open ? (
        <NoticeForm
          onComplete={(success) => {
            setProgress(100);
            if (success) {
              setSnackbarMessage("Tiedote tallennettu onnistuneesti!");
              setSnackbarSeverity("success");
            } else {
              setSnackbarMessage("Virhe tallennettaessa tiedotetta.");
              setSnackbarSeverity("error");
            }
            setOpenSnackbar(true);
          }}
          setOpenSnackbar={setOpenSnackbar}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
        />
      ) : (
        <>
          <NoticeList
            rows={notices}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
          />
        </>
      )}

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={() => setOpenSnackbar(false)}
      />
    </React.Fragment>
  );
}

export default Notices;
